/**
 * CASEWORK STUDIO — fixtures follow their box (2026-10-09, Matt: "fix the gaps").
 *
 * A sink, faucet, pot filler or wall light placed from the Studio keeps its spot ON THE BOX
 * (`studioPlace`: box-local x / y from the box's back-centre, and its turn relative to the box),
 * and is re-seated from it whenever the box moves: Move / Rotate / Mirror of the run, and every
 * run re-layout (a division, a width, a run length — `layoutRun`). The box's own geometry is the
 * truth; the fixture's world position is derived from it, never edited in step.
 *
 * Wall lights hang on the wall BEHIND the box: re-seating finds that wall again at the light's
 * spot and puts the light on the face toward the room. When no wall stands there any more the
 * light is left where it was (it never jumps to some other wall far away).
 *
 * Lives on its own so caseworkRun (layoutRun) and elements (the transforms) can both call it
 * without an import cycle.
 */
import type * as Y from 'yjs'
import type { ElementId, ElementRecord, Path2D, Pt2 } from '../types'
import { getElement, listElements, maps, transact, type ModelDoc } from '../doc'
import { hostAt } from './hosting'
import { flattenPathPts } from '../arc'
import { polylinePointAt } from '../pathOps'

export interface StudioPlace { x: number; y?: number; turn?: number }

/** the box's frame: world point of a box-local (x along, y toward the front), its facing */
export function boxFrameOf(rec: ElementRecord | null) {
  const pt = (rec?.params.path as { nodes?: { pt: Pt2 }[] } | null)?.nodes?.[0]?.pt
  if (!rec || !pt) return null
  const deg = typeof rec.params.rotationDeg === 'number' ? rec.params.rotationDeg : 0
  const r = (deg * Math.PI) / 180, c = Math.cos(r), sn = Math.sin(r)
  return {
    rec, deg,
    at: (lx: number, ly: number): Pt2 => [pt[0] + lx * c - ly * sn, pt[1] + lx * sn + ly * c],
    front: [-sn, c] as Pt2,
  }
}

export const normDeg = (d: number) => { let x = d % 360; if (x > 180) x -= 360; if (x <= -180) x += 360; return x }

/** where a wall light at box-local x hangs: the wall behind the box, its station, and the
 *  face toward the room — null when no wall stands there */
export function wallLightSeat(model: ModelDoc, box: ElementRecord, x: number): { wallId: ElementId; levelId: string; s: number; sideFlip: boolean } | null {
  const f = boxFrameOf(box)
  if (!f) return null
  const hit = hostAt(model, 'wall', box.levelId, f.at(x, 0), 1.25)
  const wall = hit ? getElement(model, hit.id) : null
  const path = wall?.params.path as Path2D | undefined
  if (!hit || !wall || !path) return null
  const { dir } = polylinePointAt(flattenPathPts(path), hit.s)
  const flipped = wall.params.flipped === true
  const facing = (flip: boolean) => {
    const rz = Math.atan2(dir[1], dir[0]) + (flipped ? 0 : Math.PI) + (flip ? Math.PI : 0)
    return -Math.sin(rz) * f.front[0] + Math.cos(rz) * f.front[1]
  }
  return { wallId: wall.id, levelId: wall.levelId, s: hit.s, sideFlip: facing(true) > facing(false) }
}

const placeOf = (v: unknown): StudioPlace | null =>
  v && typeof v === 'object' && typeof (v as StudioPlace).x === 'number' ? (v as StudioPlace) : null

/** re-seat every Studio-placed fixture of these boxes from its spot on the box */
export function reseatStudioFixtures(model: ModelDoc, boxIds: Iterable<ElementId>): void {
  const boxes = new Set<string>(boxIds)
  if (!boxes.size) return
  // CASEWORK STUDIO stage B: a built-in appliance whose box is gone (merged away) or no longer
  // holds an appliance goes too - it never stands on its own where a cabinet used to be
  const orphans = listElements(model).filter((e) => {
    if (e.params.studioRole !== 'appliance' || typeof e.params.studioBoxId !== 'string') return false
    const b = getElement(model, e.params.studioBoxId as ElementId)
    const st = b?.params.boxStyle as Record<string, unknown> | undefined
    return !b || !st || typeof st.appliance !== 'string'
  })
  if (orphans.length) transact(model, () => { for (const o of orphans) maps(model).elements.delete(o.id) })
  const followers = listElements(model).filter((e) => typeof e.params.studioBoxId === 'string' && boxes.has(e.params.studioBoxId) && placeOf(e.params.studioPlace))
  if (!followers.length) return
  const { elements } = maps(model)
  transact(model, () => {
    for (const e of followers) {
      const box = getElement(model, e.params.studioBoxId as ElementId)
      const f = boxFrameOf(box)
      const place = placeOf(e.params.studioPlace)!
      const em = elements.get(e.id)
      const pm = em?.get('params') as Y.Map<unknown> | undefined
      if (!f || !box || !em || !pm) continue
      if (e.category === 'fixture') {
        const seat = wallLightSeat(model, box, place.x)
        if (!seat) continue
        if (em.get('hostId') !== seat.wallId) em.set('hostId', seat.wallId)
        if (em.get('levelId') !== seat.levelId) em.set('levelId', seat.levelId)
        if (Math.abs(((pm.get('hostOffsetFt') as number) ?? 0) - seat.s) > 1e-9) pm.set('hostOffsetFt', seat.s)
        if ((pm.get('sideFlip') === true) !== seat.sideFlip) pm.set('sideFlip', seat.sideFlip)
        continue
      }
      const pt = f.at(place.x, place.y ?? 0)
      const path = pm.get('path') as Path2D | undefined
      const old = path?.nodes?.[0]?.pt
      if (!old || Math.abs(old[0] - pt[0]) > 1e-9 || Math.abs(old[1] - pt[1]) > 1e-9) {
        pm.set('path', { closed: false, nodes: [{ id: path?.nodes?.[0]?.id ?? ('n' + e.id), pt }] })
      }
      const deg = normDeg(f.deg + (place.turn ?? 0))
      if (pm.get('rotationDeg') !== deg) pm.set('rotationDeg', deg)
      if (em.get('levelId') !== box.levelId) em.set('levelId', box.levelId)
      // CASEWORK STUDIO stage B: a built-in appliance is as wide as its box's opening
      if (e.params.studioRole === 'appliance') {
        const bw = box.params.widthFt
        if (typeof bw === 'number' && bw > 0 && pm.get('sizeWidthFt') !== bw) pm.set('sizeWidthFt', bw)
      }
    }
  })
}

/**
 * The selection a transform of casework carries along: fixtures standing on a selected box
 * that were placed in the Modeler (hosted on it with a position of their own — a sink set on
 * the counter by hand). Studio-placed ones are re-seated afterwards instead.
 */
export function carriedByCasework(model: ModelDoc, ids: ElementId[]): ElementId[] {
  const set = new Set<string>(ids)
  const boxes = ids.filter((id) => getElement(model, id)?.category === 'casework')
  if (!boxes.length) return ids
  const boxSet = new Set<string>(boxes)
  const out = [...ids]
  for (const e of listElements(model)) {
    if (set.has(e.id) || !e.hostId || !boxSet.has(e.hostId)) continue
    if (placeOf(e.params.studioPlace)) continue
    if (!(e.params.path as Path2D | undefined)?.nodes?.length) continue
    out.push(e.id)
  }
  return out
}
